"use client";

import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Icon } from "@iconify/react";
import DOMPurify from "dompurify";

import styles from "./support-card.module.scss";
import services from "@/store/services";
import Components from "@/components";
import utils from "@/utils";
import apis from "@/api";

const formatDate = (isoString) => {
  const date = new Date(isoString);
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();

  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";

  return `${day}${suffix} ${month}, ${year}`;
};

const TICKET_RESOLVED_MSG =
  "Hi, My query is resolved Please close this ticket Thankyou!...";

const SupportCard = ({ ticket, setSupportUnread }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(false);
  const [replies, setReplies] = useState(ticket.replies || []);

  const [resolved, setResolved] = useState(false);
  const [resolveLoading, setResolveLoading] = useState(false);

  useEffect(() => {
    const ticketsLS = utils.getFromLocalStorage("tickets") || {};
    setResolved(!!ticketsLS[ticket._id]);
  }, [ticket._id]);

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      dispatch(services.infoAlert("Please type a message before sending."));
      return;
    }

    setLoading(true);
    try {
      const res = await apis.replyToQuery({
        id: ticket._id,
        message: replyText.trim(),
      });

      if (res?.status === 200 && Array.isArray(res.data?.result)) {
        setReplies(res.data.result);
        setReplyText("");
        dispatch(services.successAlert("Message sent successfully"));
      } else {
        dispatch(services.infoAlert("Failed to send reply"));
      }
    } catch (error) {
      dispatch(services.infoAlert("Error sending reply"));
      console.error(error);
    }
    setLoading(false);
  };

  const handleTicketResolved = async (e) => {
    e.stopPropagation();
    setResolveLoading(true);
    try {
      const res = await apis.replyToQuery({
        id: ticket._id,
        message: TICKET_RESOLVED_MSG,
      });

      if (res?.status === 200) {
        if (Array.isArray(res.data?.result)) {
          setReplies(res.data.result);
        }
        const ticketsLS = utils.getFromLocalStorage("tickets") || {};
        ticketsLS[ticket._id] = true;
        utils.addToLocalStorage("tickets", ticketsLS);
        setResolved(true);
        dispatch(services.successAlert("Ticket marked as resolved"));
      } else {
        dispatch(services.infoAlert("Failed to mark ticket as resolved"));
      }
    } catch (error) {
      dispatch(services.infoAlert("Error marking ticket as resolved"));
      console.error(error);
    }
    setResolveLoading(false);
  };

  useEffect(() => {
    if (expanded && ticket?.unreadCount > 0) {
      apis.markQueryAsRead({ id: ticket._id });
    }
  }, [expanded, ticket._id, ticket?.unreadCount]);

  return (
    <article className={`${styles["support-card"]}`}>
      <header>
        <div className={styles.left}>
          <Components.Avatar
            src={ticket.profile}
            alt={ticket.name || "A"}
            loader={"md"}
            errStyle={{ fontSize: "1rem" }}
          />
          <div className={styles.textBlock}>
            <div className={styles.topRow}>
              <h4>{ticket.name}</h4>
              <span>{formatDate(ticket.timeStamp)}</span>
            </div>
            <span>#{ticket.ticketNo}</span>
          </div>
        </div>
        <h4 className={`${ticket.status ? styles.close : styles.open}`}>
          {ticket.status ? "Closed" : "Open"}
        </h4>
      </header>
      <div className={styles.content}>
        <h3>
          <span>Question:</span> {ticket.title}
        </h3>
        <p>{ticket.message}</p>
      </div>

      <footer
        onClick={() => {
          setExpanded((e) => !e);
          setSupportUnread(0);
        }}
      >
        <button
          className={`${expanded ? styles.rotated : ""}`}
          aria-label="Toggle replies"
        >
          <Icon icon={"bxs:right-arrow"} />
        </button>
        <span className={styles.count}>
          {expanded ? (
            `Hide ${replies.length === 1 ? "reply" : "replies"}`
          ) : (
            <>
              {replies.length} {replies.length === 1 ? "reply" : "replies"}
              {ticket?.unreadCount > 0 && (
                <>
                  {" • "}
                  <span className={styles.unreadCount}>
                    {ticket.unreadCount}{" "}
                    {ticket.unreadCount === 1 ? "New message" : "New messages"}
                  </span>
                </>
              )}
            </>
          )}
        </span>
      </footer>


      {!ticket.status && (
        <div className={styles.resolvedBtnWrapper}>
          <button
            className={styles.textButton}
            onClick={resolved ? undefined : handleTicketResolved}
            disabled={resolved || resolveLoading}
            id={`ticket-resolved-${ticket._id}`}
          >
            Request to Close Ticket
          </button>
        </div>
      )}

      {expanded && (
        <div className={styles.accordion}>
          {replies.map((reply, idx) => (
            <article
              key={idx}
              className={reply?.isReaded ? null : styles["support-card-unread"]}
            >
              <Components.Avatar
                src={reply.profile}
                alt={reply.name || "A"}
                loader={"md"}
                errStyle={{ fontSize: "0.8rem" }}
              />
              <div className={styles.contents}>
                <div className={styles.header}>
                  <h5>{reply.name}</h5>
                  <span>{formatDate(reply.timestamp)}</span>
                </div>
                <p
                  className={styles.message}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(utils.linkify(reply.message), {
                      ALLOWED_TAGS: ["a"],
                      ALLOWED_ATTR: ["href", "target", "rel"],
                    }),
                  }}
                ></p>
              </div>
            </article>
          ))}

          {replies.length === 0 ? (
            <div className={styles.noReply}>
              We are actively working on resolving your queries. We will get
              back to you in 5–7 days.
            </div>
          ) : (
            <div className={styles.actions}>
              <Components.TextArea
                rows={2}
                type="text"
                value={replyText}
                disabled={loading}
                placeholder="Type your message..."
                onChange={(e) => setReplyText(e.target.value)}
              />
              <Components.Button
                name={loading ? "Sending..." : "Send"}
                handleClick={handleSendReply}
                className={styles.sendBtn}
                disable={loading}
                id={`send-reply-${ticket._id}`}
              />
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default SupportCard;
